/**
 * ░█▀▄░▀▀█░▀▀█░█▀█░█▀█░█▀█
 * ░█░█░░▀▄░░▀▄░█▀▀░█▀█░█░█
 * ░▀▀░░▀▀░░▀▀░░▀░░░▀░▀░▀░▀
 *
 * @author d33pan on 7/8/2026
 */
import {DynamicSectionFormSchema} from "../../../dynamic-form-v2/form-schema.types";

export const bankOnboardingSchema: DynamicSectionFormSchema = [
  {
    title: 'Basic information',
    description: 'Core identity details for the institution',
    icon: 'fa-solid fa-house',
    className: 'lg:col-span-6',
    fields: [
      {
        type: 'TEXT',
        key: 'bankName',
        label: 'Bank name',
        placeholder: 'Enter bank name',
        validators: { required: true },
        className: 'col-span-12',
      },
      {
        type: 'TEXT',
        key: 'swiftCode',
        label: 'Swift code',
        placeholder: 'Enter swift code',
        validators: { required: true, pattern: '^[A-Z0-9]{8,11}$' },
        errorMessages: { pattern: 'Enter a valid 8–11 character SWIFT code.' },
        className: 'col-span-12',
      },
    ],
  },
  {
    title: 'Personal Information',
    description: 'Enter your basic personal details.',
    icon: 'fa-solid fa-user',
    className: 'lg:col-span-6',
    fields: [
      {
        type: 'TEXT',
        key: 'firstName',
        label: 'First Name',
        placeholder: 'Enter first name',
        validators: { required: true },
        className: 'lg:col-span-6',
      },
      {
        type: 'TEXT',
        key: 'lastName',
        label: 'Last Name',
        placeholder: 'Enter last name',
        validators: { required: true },
        className: 'lg:col-span-6',
      },
      {
        type: 'TEXT',
        key: 'email',
        label: 'Email',
        placeholder: 'Enter email address',
        validators: { required: true },
        className: 'lg:col-span-12',
      },
    ],
  },

  // One full-width card with a 4-across grid (md:col-span-3) to show a denser layout
  {
    title: 'Loan Details',
    description: 'Facility terms and calculated totals',
    icon: 'fa-solid fa-sack-dollar',
    className: 'col-span-12',
    fields: [
      {
        type: 'DROPDOWN',
        key: 'loanType',
        label: 'Loan type',
        validators: { required: true },
        className: 'md:col-span-3',
        options: [
          { key: 'PERSONAL', value: 'Personal' },
          { key: 'HOME', value: 'Home' },
          { key: 'AUTO', value: 'Auto' },
          { key: 'BUSINESS', value: 'Business' },
        ],
      },
      {
        type: 'AMOUNT',
        key: 'loanAmount',
        typeKey: 'loanAmountType',
        label: 'Loan amount',
        placeholder: 'Enter amount',
        validators: { required: true, min: 0 },
        className: 'md:col-span-3',
      },
      {
        type: 'NUMBER',
        key: 'interestRate',
        label: 'Interest rate (%)',
        placeholder: 'e.g. 8.5',
        validators: { required: true, min: 0, max: 100 },
        className: 'md:col-span-3',
      },
      {
        type: 'NUMBER',
        key: 'tenureMonths',
        label: 'Tenure (months)',
        validators: { required: true, min: 1 },
        className: 'md:col-span-3',
      },
      {
        type: 'CALENDAR',
        key: 'disbursementDate',
        label: 'Disbursement date',
        validators: { required: true },
        className: 'md:col-span-3',
      },
      // Calculated: recomputes automatically whenever loanAmount or interestRate change
      {
        type: 'NUMBER',
        key: 'totalPayable',
        label: 'Total payable (est.)',
        className: 'md:col-span-3',
        calculated: {
          formula: 'loanAmount + (loanAmount * interestRate / 100)',
          dependencies: ['loanAmount', 'interestRate'],
        },
      },
      {
        type: 'CHECKBOX',
        key: 'hasCoApplicant',
        label: 'This loan has a co-applicant',
        className: 'col-span-12',
      },
      {
        type: 'TEXT',
        key: 'coApplicantName',
        label: 'Co-applicant name',
        placeholder: 'Enter co-applicant name',
        validators: { required: true },
        className: 'md:col-span-3',
        dependsOn: { key: 'hasCoApplicant', value: true, condition: 'EQUALS' },
      },
    ],
  },

  {
    title: 'Repayment Preferences',
    description: 'How and when repayments are collected',
    icon: 'fa-solid fa-rotate',
    className: 'lg:col-span-6',
    fields: [
      {
        type: 'RADIO',
        key: 'repaymentMode',
        label: 'Repayment mode',
        validators: { required: true },
        className: 'col-span-12',
        options: [
          { key: 'AUTO_DEBIT', value: 'Auto debit' },
          { key: 'MANUAL', value: 'Manual' },
        ],
      },
      {
        type: 'TOGGLE',
        key: 'autoRenew',
        label: 'Auto-renew facility',
        className: 'col-span-12',
      },
      {
        type: 'TEXTAREA',
        key: 'notes',
        label: 'Internal notes',
        placeholder: 'Anything the reviewer should know',
        className: 'md:col-span-12',
      },
      {
        type: 'CHECKBOX',
        key: 'agreeTerms',
        label: 'Applicant agrees to the terms & conditions',
        validators: { required: true },
        className: 'md:col-span-12',
      },
    ],
  },
  // {
  //   title: 'Documents & Access',
  //   description: 'Uploads and account credentials',
  //   icon: 'fa-solid fa-file-shield',
  //   className: 'lg:col-span-6',
  //   fields: [
  //     {
  //       type: '',
  //       key: 'idProof',
  //       label: 'ID proof',
  //       accept: '.pdf,.jpg,.png',
  //       validators: { required: true },
  //       className: 'md:col-span-6',
  //     },
  //     {
  //       type: 'TIME',
  //       key: 'appointmentTime',
  //       label: 'Branch visit time',
  //       className: 'md:col-span-6',
  //     },
  //     {
  //       type: 'NEPALI_INPUT',
  //       key: 'nomineeNameNepali',
  //       label: 'Nominee name (Nepali)',
  //       className: 'md:col-span-6',
  //     },
  //     {
  //       type: 'PASSWORD',
  //       key: 'portalPassword',
  //       label: 'Portal password',
  //       validators: { required: true, custom: 'passwordStrength' },
  //       className: 'md:col-span-6',
  //     },
  //     {
  //       type: 'RICHTEXTEDITOR',
  //       key: 'remarks',
  //       label: 'Remarks',
  //       className: 'md:col-span-12',
  //     },
  //   ],
  // },
];
